/**
 * Field builder: the structural editor for the intake form.
 *
 * It layers live admin choices over the code schema: fields can be edited, hidden and
 * reordered, sections can be renamed/reordered/retired, and movable fields can cross pages.
 * Notice banners and upload blocks use stable synthetic paths, so they participate in those
 * same layout controls while upload storage remains keyed by attachment category.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCorners, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useCollection, useDoc } from '../../lib/useFirestore';
import { REQUIREMENT, isUsableDefault } from '../../lib/fieldRules';
import { isLockableDerivationKind } from '../../lib/deriveMatter';
import AdminRequirementSettings from './AdminFieldRules';
import { fieldByPath } from '../../lib/conditionBuilderModel';
import {
  CUSTOM_FIELD_TYPES,
  DEFAULT_FIELD_LAYOUT,
  buildBuilderSections,
  canDeleteCustomSection,
  changeCustomFieldType,
  conditionBuilderFieldList,
  customFieldPath,
  customSectionId,
  attachmentCategorySlug,
  customFieldTypeNeedsOptions,
  customFieldTypeNeedsRange,
  customFieldTypeNeedsSubFields,
  PERCENT_DEFAULTS,
  sanitizeSubFieldRows,
  deletedBaseFields,
  isUniqueAttachmentCategory,
  isOverridable,
  normalizeLayout,
  sanitizeLayoutOptions,
} from '../../lib/fieldLayout';
import { buildDeleteWarnings } from '../../lib/conditionPaths';
import { derivationRuleSummary } from '../../lib/derivationSummary';
import {
  allFeeds,
  feedLookupAttributes,
  feedLookupDirectories,
  feedLookupId,
  lookupFeedId,
} from '../../lib/feedRegistry';
import { distinctTitleTokens } from '../../lib/peopleTitles';
import { FORM_SECTIONS } from '../../lib/formSchema';
import { FIELD_WIDTHS, fieldWidth } from '../../lib/fieldGrid';
import { NOTICE_VARIANTS, hasEditableOptions } from '../../lib/fieldFormat';
import ConditionBuilder from '../../components/ConditionBuilder';
import OptionRowsEditor from '../../components/OptionRowsEditor';
import SubFieldRowsEditor from '../../components/SubFieldRowsEditor';
import { FormatControls } from '../../components/LayoutEditor';

const LEVELS = [
  ['', 'Use field rules'],
  [REQUIREMENT.REQUIRED, 'Required'],
  [REQUIREMENT.SOFT_REQUIRED, 'Soft required'],
  [REQUIREMENT.OPTIONAL, 'Optional'],
];

// Includes 'plain' (no border or background); defined once in lib/fieldFormat.js.
/** Control types with an "unselected" state whose prompt text is worth relabelling. */
const SUPPORTS_PLACEHOLDER = ['select', 'userSelect', 'personSearch', 'peopleMultiSelect', 'originationSplit'];

/**
 * Field types a starting value can sensibly be typed into. A default is a LITERAL — the same
 * restriction settings/fieldDerivations puts on a setDefault rule’s value — so types whose value
 * is a list, an object or a file are left out rather than given a box that cannot work.
 */
const SUPPORTS_DEFAULT = ['text', 'textarea', 'select', 'radio', 'yesno', 'number', 'money', 'percent', 'date', 'email', 'tel'];

/** What the admin sees in the box: booleans as Yes/No, everything else as itself. */
function defaultValueText(value) {
  if (value === undefined || value === null) return '';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return String(value);
}

/**
 * Turn what was typed into the literal the form will receive, using the field’s type. Storing
 * "Yes" as the string "Yes" into a yes/no field would set a value that field can never match,
 * which shows up as a control that looks unanswered while holding a value.
 */
function parseDefaultValue(field, text) {
  const raw = String(text).trim();
  if (raw === '') return undefined;
  if (field.type === 'yesno') {
    if (/^(yes|true|y)$/i.test(raw)) return true;
    if (/^(no|false|n)$/i.test(raw)) return false;
    return undefined;
  }
  if (field.type === 'number' || field.type === 'money' || field.type === 'percent') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return raw;
}
// The people choosers whose list can be limited by roster title (lib/peopleTitles.js).
// originationSplit included: its per-row dropdown is a people chooser like the others, so
// it takes the same roster-title filter (attorneys-only origination, in practice).
const PEOPLE_CHOOSER_TYPES = ['userSelect', 'personSearch', 'peopleMultiSelect', 'originationSplit'];

const sectionDragId = (id) => `section:${id}`;
const fieldDragId = (path) => `field:${path}`;
const canMove = (field) => field.isCustom || isOverridable(field);

/**
 * Sections and field rows share a DndContext, but their targets must never compete.
 * A page drag should only measure pages; a field drag measures rows plus its empty-page
 * container target. Otherwise nested field rows frequently beat their parent page in
 * closest-corners hit testing.
 */
function collisionDetectionByType({ active, droppableContainers, ...args }) {
  const activeType = active.data.current?.type;
  const candidates = droppableContainers.filter((container) => {
    const type = container.data.current?.type;
    if (container.id === active.id) return false;
    if (activeType === 'field') return type === 'field' || type === 'field-container';
    return type === activeType;
  });
  return closestCorners({ ...args, droppableContainers: candidates });
}

export default function AdminFieldBuilder() {
  const { data: stored, loading } = useDoc('settings/fieldLayout');
  const practiceGroups = useCollection('practiceGroups');
  // Distinct roster titles feed the people-chooser "Limit to titles" chips.
  const roster = useCollection('roster');
  const feedsDoc = useDoc('settings/feeds');
  const { data: areasOfLawDoc } = useDoc('settings/areasOfLaw');
  // Used to NAME the approval steps a field deletion would affect in its confirmation.
  // The loading flag gates the Delete confirm so warnings are never silently empty.
  const { data: approvalFlowDoc, loading: approvalFlowLoading } = useDoc('settings/approvalFlow');
  const { run, busy, error, saved } = useCallable();
  const [tab, setTab] = useState('layout');
  const [layout, setLayout] = useState(null);
  const [pendingDeleteSectionId, setPendingDeleteSectionId] = useState('');
  const [pageDeleteError, setPageDeleteError] = useState('');
  // Set right after "+ Add page" so the new page — appended at the bottom of a
  // potentially long list — scrolls into view and briefly highlights itself instead of
  // silently landing off-screen with no feedback that anything happened.
  const [highlightSectionId, setHighlightSectionId] = useState('');
  const sectionRefs = useRef(new Map());
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!loading && !layout) {
      setLayout(stored ? normalizeLayout(structuredClone(stored)) : { ...DEFAULT_FIELD_LAYOUT });
    }
  }, [stored, loading, layout]);

  useEffect(() => {
    if (!highlightSectionId) return;
    const node = sectionRefs.current.get(highlightSectionId);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.querySelector('input[aria-label^="Page title"]')?.focus();
    }
    const timer = setTimeout(() => setHighlightSectionId(''), 2000);
    return () => clearTimeout(timer);
  }, [highlightSectionId, layout]);

  const conditionFields = useMemo(
    () => conditionBuilderFieldList(FORM_SECTIONS, layout),
    [layout]
  );
  const areasOfLaw = useMemo(
    () => (areasOfLawDoc?.items || []).filter((item) => item.isActive !== false),
    [areasOfLawDoc]
  );

  if (loading || !layout) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading layout…</p>;

  const sections = buildBuilderSections(FORM_SECTIONS, layout);
  // One flat field context supports dnd-kit's multiple-containers pattern. This lookup
  // follows live layout state after handleDragOver moves a field to a new page.
  const movableFieldIds = [];
  const fieldSectionByDragId = new Map();
  sections.forEach((section) => {
    section.fields.filter(canMove).forEach((field) => {
      const id = fieldDragId(field.path);
      movableFieldIds.push(id);
      fieldSectionByDragId.set(id, section.id);
    });
  });
  const sortableIds = [...sections.map((section) => sectionDragId(section.id)), ...movableFieldIds];
  const takenPaths = [
    ...FORM_SECTIONS.flatMap((s) => s.fields.map((f) => f.path).filter(Boolean)),
    ...layout.customFields.map((f) => f.path),
  ];

  const addSection = () => {
    setPageDeleteError('');
    let newId = '';
    setLayout((prev) => {
      const currentSections = buildBuilderSections(FORM_SECTIONS, prev);
      const nextOrder = Math.max(-1, ...currentSections.map((section) => section.order).filter(Number.isFinite)) + 1;
      const id = customSectionId(
        'New page',
        currentSections.map((section) => section.id)
      );
      newId = id;
      return {
        ...prev,
        customSections: [
          ...prev.customSections,
          { id, title: 'New page', order: nextOrder, isActive: true },
        ],
      };
    });
    // The new page always lands at the bottom of the list, which can otherwise be
    // far off-screen with no sign anything happened — scroll it into view and give it a
    // brief highlight so "Add page" reads as an obvious, confirmed action.
    setHighlightSectionId(newId);
  };

  const patchOverride = (path, key, value) =>
    setLayout((prev) => {
      const existing = prev.overrides[path] || {};
      // `undefined` removes the override key entirely — "go back to what the code ships"
      // (a cleared visibility condition, restored built-in repeatable columns) — rather
      // than storing undefined, which Firestore rejects and merge logic would misread.
      if (value === undefined) {
        const { [key]: _dropped, ...withoutKey } = existing;
        return {
          ...prev,
          overrides: { ...prev.overrides, [path]: { isActive: true, ...withoutKey } },
        };
      }
      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          [path]: { isActive: true, ...existing, [key]: value },
        },
      };
    });

  /**
   * Page show/hide logic.  clears the override (back to the coded rule);
   *  is a stored 'always show', which is how a page whose coded condition
   * references a removed field is recovered.
   */
  const setSectionConditionFor = (sectionId, condition) =>
    setLayout((prev) => {
      const existing = prev.sectionOverrides[sectionId] || {};
      if (condition === undefined) {
        const { visibleIf, ...rest } = existing;
        return { ...prev, sectionOverrides: { ...prev.sectionOverrides, [sectionId]: rest } };
      }
      return {
        ...prev,
        sectionOverrides: { ...prev.sectionOverrides, [sectionId]: { ...existing, visibleIf: condition } },
      };
    });

  const patchCustom = (path, key, value) =>
    setLayout((prev) => ({
      ...prev,
      customFields: prev.customFields.map((f) => {
        if (f.path !== path) return f;
        if (key === 'visibleIf' && value === undefined) {
          const { visibleIf, ...withoutVisibleIf } = f;
          return withoutVisibleIf;
        }
        return { ...f, [key]: value };
      }),
    }));

  /**
   * Retype an existing custom field in place. Only custom fields: a base field's type is
   * defined in formSchema.js, and every saved answer plus every reader (PDF, manifest,
   * conditions) is written against that shape — see changeCustomFieldType's doc comment.
   */
  const changeCustomType = (field, nextType) =>
    setLayout((prev) => ({
      ...prev,
      customFields: prev.customFields.map((f) =>
        f.path === field.path ? changeCustomFieldType(f, nextType) : f
      ),
    }));

  const patchSection = (sectionId, key, value) =>
    setLayout((prev) => ({
      ...prev,
      sectionOverrides: {
        ...prev.sectionOverrides,
        [sectionId]: { ...prev.sectionOverrides[sectionId], [key]: value },
      },
    }));

  const removeCustom = (path) =>
    setLayout((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((f) => f.path !== path),
    }));

  /**
   * Delete for ANY field. Custom fields lose their record; built-in fields get a
   * `deleted: true` tombstone in their override, which drops them from the form AND the
   * builder view. Restorable from the "Deleted fields" panel below the page list.
   */
  const deleteAnyField = (field) => {
    if (field.isCustom) removeCustom(field.path);
    else patchOverride(field.path, 'deleted', true);
  };

  const restoreDeletedField = (path) =>
    setLayout((prev) => {
      const existing = prev.overrides[path];
      if (!existing) return prev;
      const { deleted, ...rest } = existing;
      return { ...prev, overrides: { ...prev.overrides, [path]: rest } };
    });

  /**
   * Named consequences for the delete confirmation — approval gates, reveal rules and
   * structural submission checks, computed against the LIVE flow and the MERGED sections
   * (so admin-authored conditions and custom fields count). One shared implementation:
   * lib/conditionPaths.js buildDeleteWarnings.
   */
  const deleteWarningsFor = (field) =>
    buildDeleteWarnings(field, { flow: approvalFlowDoc, sections });

  const addCustom = (draft) =>
    setLayout((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        {
          path: customFieldPath(draft.label, takenPaths),
          label: draft.label.trim(),
          type: draft.type,
          sectionId: draft.sectionId,
          isActive: true,
          order: 1000 + prev.customFields.length,
          requirement: draft.required ? REQUIREMENT.REQUIRED : REQUIREMENT.OPTIONAL,
          helpText: '',
          preventClone: false,
          verificationRequired: false,
          ...(customFieldTypeNeedsOptions(draft.type) ? { options: draft.options } : null),
          ...(draft.type === 'notice' ? {
            text: draft.text.trim(),
            variant: draft.variant,
            ...(draft.visibleIf !== undefined ? { visibleIf: draft.visibleIf } : null),
          } : null),
          ...(draft.type === 'attachments' ? {
            category: draft.category,
            helpText: draft.helpText.trim(),
          } : null),
          // A repeating group's columns and Add-button label. Without these the field
          // would persist as a repeatable with no columns — a dead control on the form.
          ...(customFieldTypeNeedsSubFields(draft.type) ? {
            fields: sanitizeSubFieldRows(draft.fields),
            ...(draft.addLabel ? { addLabel: draft.addLabel } : null),
          } : null),
          ...(draft.newRow ? { newRow: true } : null),
          ...(draft.fullWidth ? { fullWidth: true } : null),
          // The title filter chosen while inserting a people picker (also editable later
          // on the field's row) — stored only when non-empty, like every other filter.
          ...(Array.isArray(draft.titleFilter) && draft.titleFilter.length
            ? { titleFilter: draft.titleFilter }
            : null),
        },
      ],
    }));

  /** Keep the established arrow controls, including their pin-aware in-section behaviour. */
  const move = (sectionId, path, delta) => {
    const rows = sections.find((section) => section.id === sectionId).fields;
    const slots = [];
    const movable = [];
    rows.forEach((field, index) => {
      if (canMove(field)) {
        slots.push(index);
        movable.push(field);
      }
    });
    const from = movable.findIndex((field) => field.path === path);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= movable.length) return;
    [movable[from], movable[to]] = [movable[to], movable[from]];
    applyFieldOrders({ [sectionId]: movable }, { [sectionId]: slots });
  };

  /**
   * Persist a field order for each affected section. A defensive future pathless field
   * stays in its existing slot; added cross-page fields append after movable fields.
   */
  const applyFieldOrders = (orderedBySection, slotsBySection = {}) => {
    setLayout((prev) => {
      const overrides = { ...prev.overrides };
      const customUpdates = new Map();
      for (const [sectionId, ordered] of Object.entries(orderedBySection)) {
        const current = sections.find((section) => section.id === sectionId);
        const slots = [...(slotsBySection[sectionId] || current.fields
          .map((field, index) => (canMove(field) ? index : null))
          .filter((index) => index !== null))];
        while (slots.length < ordered.length) slots.push((slots.at(-1) ?? -1) + 1);
        ordered.forEach((field, index) => {
          const order = slots[index];
          if (field.isCustom) customUpdates.set(field.path, { sectionId, order });
          else {
            overrides[field.path] = {
              isActive: true,
              ...overrides[field.path],
              sectionId,
              order,
            };
          }
        });
      }
      return {
        ...prev,
        overrides,
        customFields: prev.customFields.map((field) =>
          customUpdates.has(field.path) ? { ...field, ...customUpdates.get(field.path) } : field
        ),
      };
    });
  };

  const reorderSections = (activeId, overId) => {
    const from = sections.findIndex((section) => section.id === activeId);
    const to = sections.findIndex((section) => section.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const ordered = [...sections];
    const [moving] = ordered.splice(from, 1);
    ordered.splice(to, 0, moving);
    setLayout((prev) => ({
      ...prev,
      sectionOverrides: {
        ...prev.sectionOverrides,
        ...Object.fromEntries(
          ordered.map((section, index) => [
            section.id,
            { ...prev.sectionOverrides[section.id], order: index },
          ])
        ),
      },
    }));
  };

  const requestDeleteSection = (section) => {
    if (!section.isCustom) return;
    if (!canDeleteCustomSection(section)) {
      setPendingDeleteSectionId('');
      setPageDeleteError(
        `Move or delete this page's ${section.fields.length} field${section.fields.length === 1 ? '' : 's'} before deleting the page.`
      );
      return;
    }
    setPageDeleteError('');
    setPendingDeleteSectionId(section.id);
  };

  const deleteSection = (section) => {
    if (!canDeleteCustomSection(section)) {
      requestDeleteSection(section);
      return;
    }
    setLayout((prev) => {
      const { [section.id]: removedOverride, ...remainingSectionOverrides } = prev.sectionOverrides;
      return {
        ...prev,
        // Fields must be gone before this point. Blocking is safer than guessing a new
        // destination and keeps a deletion from silently changing the intake's meaning.
        customSections: prev.customSections.filter((candidate) => candidate.id !== section.id),
        sectionOverrides: remainingSectionOverrides,
      };
    });
    setPendingDeleteSectionId('');
    setPageDeleteError('');
  };

  /** Dropdown-driven equivalent of a cross-page drag: append the field at the end of the destination page. */
  const moveFieldToSection = (field, sourceId, destinationId) => {
    if (!destinationId || destinationId === sourceId) return;
    const source = sections.find((section) => section.id === sourceId);
    const destination = sections.find((section) => section.id === destinationId);
    if (!source || !destination) return;
    const sourceFields = source.fields.filter(canMove).filter((entry) => entry.path !== field.path);
    const destinationFields = [...destination.fields.filter(canMove), field];
    applyFieldOrders({ [sourceId]: sourceFields, [destinationId]: destinationFields });
  };

  const moveFieldByDrag = (activePath, sourceId, over) => {
    const source = sections.find((section) => section.id === sourceId);
    const destinationId = over.data.current?.type === 'section'
      ? over.data.current.sectionId
      : over.data.current?.sectionId;
    const destination = sections.find((section) => section.id === destinationId);
    const field = source?.fields.find((entry) => entry.path === activePath);
    if (!source || !destination || !field || !canMove(field)) return;

    const sourceFields = source.fields.filter(canMove).filter((entry) => entry.path !== activePath);
    const destinationFields = (sourceId === destinationId ? sourceFields : destination.fields.filter(canMove));
    const destinationIndex = over.data.current?.type === 'field'
      ? Math.max(0, destinationFields.findIndex((entry) => entry.path === over.data.current.path))
      : destinationFields.length;
    destinationFields.splice(destinationIndex < 0 ? destinationFields.length : destinationIndex, 0, field);

    if (sourceId === destinationId) {
      applyFieldOrders({ [sourceId]: destinationFields });
    } else {
      applyFieldOrders({ [sourceId]: sourceFields, [destinationId]: destinationFields });
    }
  };

  const handleDragOver = ({ active, over }) => {
    if (!over || active.data.current?.type !== 'field') return;
    const sourceId = fieldSectionByDragId.get(active.id) || active.data.current.sectionId;
    const destinationId = over.data.current?.sectionId;
    // Reordering within a page is left to SortableContext's normal drag-end handling.
    // Crossing into another page must be reflected live so its rows can become valid,
    // continuously measured targets for the remainder of this drag.
    if (sourceId && destinationId && sourceId !== destinationId) {
      moveFieldByDrag(active.data.current.path, sourceId, over);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const data = active.data.current;
    if (data?.type === 'section' && over.data.current?.type === 'section') {
      reorderSections(data.sectionId, over.data.current.sectionId);
    }
    if (data?.type === 'field') {
      const sourceId = fieldSectionByDragId.get(active.id) || data.sectionId;
      moveFieldByDrag(data.path, sourceId, over);
    }
  };

  const save = () => {
    // Blank/duplicate option rows are dropped at save time, and a base override emptied
    // of options reverts to the code-shipped list — see fieldLayout.js sanitizeLayoutOptions.
    const sanitized = sanitizeLayoutOptions(layout);
    return run('adminUpdateSettings', {
      docId: 'fieldLayout',
      value: {
        version: (stored?.version || 0) + 1,
        overrides: sanitized.overrides,
        customFields: sanitized.customFields,
        customSections: sanitized.customSections,
        sectionOverrides: sanitized.sectionOverrides,
      },
    });
  };

  const tabButtonClass = (id) =>
    id === tab
      ? 'border-b-2 border-firm-700 pb-2 text-sm font-semibold text-firm-700 dark:border-firm-100 dark:text-firm-100'
      : 'border-b-2 border-transparent pb-2 text-sm font-medium text-slate-500 hover:text-firm-700 dark:text-slate-400 dark:hover:text-firm-100';

  return (
    <div className="space-y-4">
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-700">
        <button type="button" onClick={() => setTab('layout')} className={tabButtonClass('layout')}>
          Layout
        </button>
        <button type="button" onClick={() => setTab('rules')} className={tabButtonClass('rules')}>
          Auto-fill rules
        </button>
        {/*
          The old Field rules page folded in here. Its per-field table set requirement, help
          text, starting value and greyed-out — all four of which the Layout tab already sets on
          the field itself. What was left is firm-wide and has no per-field home, so it becomes
          a tab rather than a page of its own.
        */}
        <button type="button" onClick={() => setTab('markers')} className={tabButtonClass('markers')}>
          Markers &amp; explanations
        </button>
      </div>

      {tab === 'markers' ? (
        <AdminRequirementSettings />
      ) : tab === 'rules' ? (
        <FieldDerivationsPanel
          conditionFields={conditionFields}
          practiceGroups={practiceGroups.data.map((group) => ({ id: group.id, name: group.name || group.id }))}
          areasOfLaw={areasOfLaw}
        />
      ) : (
    <div className="space-y-4">
      <div className="sticky top-3 z-20 ml-auto flex w-fit items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md dark:border-slate-700 dark:bg-slate-900">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save field layout
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <p className="rounded border border-slate-200 bg-white p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        Drag page handles to reorder pages and field handles to reorder fields or move them to another page — or use the <strong>Move to page</strong> dropdown on any field for a no-drag option. Add a custom page when you need a new step; the new page scrolls into view and highlights briefly so it's easy to spot at the bottom of the list. Empty custom pages can be deleted after confirmation. Code-defined pages cannot be deleted, but either kind can be retired with <strong>Show page</strong>. The up/down arrows remain available for precise keyboard-free changes. <strong>Never copy</strong> keeps a field out of “Clone this matter” and templates; <strong>Verification required</strong> adds it to the pre-submit checklist. Notice banners and upload blocks can be moved and customized like other fields; existing upload categories remain fixed so their workflow behavior stays safe.
      </p>

      <button
        type="button"
        onClick={addSection}
        className="rounded border border-dashed border-slate-400 px-3 py-1 text-xs text-slate-600 hover:border-firm-500 hover:text-firm-700 dark:border-slate-500 dark:text-slate-300 dark:hover:text-firm-100"
      >
        + Add page
      </button>
      {pageDeleteError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{pageDeleteError}</p>}

      <AddFieldForm
        sections={sections}
        onAdd={addCustom}
        conditionFields={conditionFields}
        practiceGroups={practiceGroups.data.map((group) => ({ id: group.id, name: group.name || group.id }))}
        areasOfLaw={areasOfLaw}
        roster={roster.data}
        feeds={feedsDoc.data}
        existingAttachmentCategories={sections.flatMap((section) =>
          section.fields
            .filter((field) => field.type === 'attachments' && field.category)
            .map((field) => field.category)
        )}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionByType}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                onPatchSection={patchSection}
                onSetSectionCondition={setSectionConditionFor}
                onRequestDeleteSection={() => requestDeleteSection(section)}
                onDeleteSection={() => deleteSection(section)}
                pendingDelete={pendingDeleteSectionId === section.id}
                onCancelDelete={() => setPendingDeleteSectionId('')}
                onPatch={(field, key, value) =>
                  field.isCustom ? patchCustom(field.path, key, value) : patchOverride(field.path, key, value)
                }
                onChangeType={(field, nextType) => changeCustomType(field, nextType)}
                onMove={(field, delta) => move(section.id, field.path, delta)}
                onDelete={(field) => deleteAnyField(field)}
                deleteWarningsFor={deleteWarningsFor}
                deleteDisabled={approvalFlowLoading}
                onMoveToSection={(field, destinationId) => moveFieldToSection(field, section.id, destinationId)}
                allSections={sections}
                conditionFields={conditionFields}
                practiceGroups={practiceGroups.data.map((group) => ({
                  id: group.id,
                  name: group.name || group.id,
                }))}
                areasOfLaw={areasOfLaw}
                roster={roster.data}
                feeds={feedsDoc.data}
                registerRef={(node) => {
                  if (node) sectionRefs.current.set(section.id, node);
                  else sectionRefs.current.delete(section.id);
                }}
                isHighlighted={highlightSectionId === section.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {deletedBaseFields(FORM_SECTIONS, layout).length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-200">
            Deleted fields ({deletedBaseFields(FORM_SECTIONS, layout).length}) — restorable
          </summary>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Built-in fields you have deleted. They are gone from the form and the pages above,
            but their definitions ship with the app, so they can always be restored. Saved
            answers on existing matters were not erased.
          </p>
          <ul className="mt-2 space-y-1">
            {deletedBaseFields(FORM_SECTIONS, layout).map((tombstone) => (
              <li key={tombstone.path} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <span>
                  {tombstone.label}
                  <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">
                    ({tombstone.sectionTitle} · {tombstone.type})
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => restoreDeletedField(tombstone.path)}
                  className="text-xs text-firm-700 hover:underline dark:text-firm-100"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
      )}
    </div>
  );
}

const DERIVATION_RULE_KINDS = [
  ['setDefault', 'Set a default'],
  ['mirror', 'Mirror another field'],
  ['lookup', 'Look up a related record'],
  ['appendPeople', 'Copy people into another field (add only)'],
];

const LOOKUP_DIRECTORIES = [
  ['roster', 'Roster'],
  ['practiceGroups', 'Practice groups'],
  ['areasOfLaw', 'Areas of law'],
];

// A curated allowlist, not a free-text field: an admin authoring a rule should never need
// to know (or risk mistyping) a directory record's real property name.
const LOOKUP_ATTRIBUTES = {
  roster: [
    ['displayName', 'Display name'],
    ['email', 'Email'],
  ],
  practiceGroups: [
    ['name', 'Name'],
    ['practiceGroupLeaderUid', 'Practice group leader'],
  ],
  areasOfLaw: [['name', 'Name']],
};

const newDerivationRule = (kind) => ({
  id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  kind,
  enabled: true,
  targetPath: '',
  ...(kind === 'setDefault' ? { when: true, value: '' } : null),
  ...(kind === 'mirror' ? { sourcePath: '' } : null),
  ...(kind === 'lookup' ? { sourcePath: '', lookupIn: 'roster', attribute: 'displayName' } : null),
});

/**
 * Admin-configurable cross-field logic (settings/fieldDerivations) — see
 * web/src/lib/deriveMatter.js's applyConfiguredDerivations for the three rule kinds this
 * edits and their exact semantics. Client-only: these run live as the form is filled in,
 * the same as the hardcoded rules in that file.
 */
function FieldDerivationsPanel({ conditionFields, practiceGroups, areasOfLaw }) {
  const { data: stored, loading } = useDoc('settings/fieldDerivations');
  // The feeds' own column mappings are what name the things a rule can copy out of a client
  // record, so this page reads them rather than carrying a second list that could disagree.
  const { data: feedsDoc } = useDoc('settings/feeds');
  const { run, busy, error, saved } = useCallable();
  const [rules, setRules] = useState(null);
  // What the document said when this page last took a copy of it. Kept so an edit made
  // elsewhere can be told apart from an edit made here — see below.
  const [baseline, setBaseline] = useState(null);

  const storedRules = JSON.stringify(Array.isArray(stored?.rules) ? stored.rules : []);
  const edited = rules !== null && JSON.stringify(rules) !== baseline;

  /**
   * Adopt what the document says, unless there are unsaved edits on screen.
   *
   * This used to take ONE snapshot and never look again, which quietly turned Save into a
   * rollback: a page opened before the rules changed would write its stale copy straight back
   * over them, destroying every rule added since without a word. It is the same shape as every
   * other failure in this feature — nothing errors, and the result looks like a normal save.
   *
   * With unsaved edits the newer version is NOT adopted, because throwing away what someone is
   * in the middle of typing is its own kind of data loss. They get told instead.
   */
  useEffect(() => {
    if (loading) return;
    if (rules === null || !edited) {
      setRules(JSON.parse(storedRules));
      setBaseline(storedRules);
    }
    // `edited` is derived from rules/baseline and re-read on every render; listing it here would
    // re-run this effect on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedRules, loading]);

  if (loading || !rules) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading auto-fill rules…</p>;
  }

  const changedElsewhere = edited && storedRules !== baseline;

  const patchRule = (id, patch) => setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRule = (id) => setRules((prev) => prev.filter((r) => r.id !== id));
  const addRule = (kind) => setRules((prev) => [...prev, newDerivationRule(kind)]);
  const save = () => run('adminUpdateSettings', { docId: 'fieldDerivations', value: { rules } });

  return (
    <div className="space-y-4">
      <div className="sticky top-3 z-20 ml-auto flex w-fit items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md dark:border-slate-700 dark:bg-slate-900">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save auto-fill rules
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      {changedElsewhere && (
        <p className="rounded border border-red-400 bg-red-50 p-3 text-xs text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
          <strong>These rules were changed somewhere else since you opened this page.</strong>{' '}
          Saving now would overwrite that change with what is on your screen. Reload the page to
          see the current rules — you will lose the edits you have made here, which are not saved.
        </p>
      )}
      <p className="rounded border border-slate-200 bg-white p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        These rules run every time the form changes, in the order listed below — top to bottom.{' '}
        <strong>Set a default</strong> fills a field once a condition becomes true, but never overwrites an
        answer already given. <strong>Mirror another field</strong> keeps one field matching another until
        someone deliberately types something different, then leaves it alone.{' '}
        <strong>Look up a related record</strong> fills a field from Roster, Practice groups, or Areas of law
        based on another field's selection. Avoid two rules that mirror each other's fields — rules run once
        per edit, not until they settle, so a mirror cycle will fight itself on every keystroke.
      </p>

      <div className="space-y-3">
        {rules.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No auto-fill rules yet.</p>}
        {rules.map((rule) => (
          <FieldDerivationRuleCard
            key={rule.id}
            rule={rule}
            conditionFields={conditionFields}
            practiceGroups={practiceGroups}
            areasOfLaw={areasOfLaw}
            storedFeeds={feedsDoc?.feeds}
            onPatch={(patch) => patchRule(rule.id, patch)}
            onRemove={() => removeRule(rule.id)}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {DERIVATION_RULE_KINDS.map(([kind, label]) => (
          <button
            key={kind}
            type="button"
            onClick={() => addRule(kind)}
            className="rounded border border-dashed border-slate-400 px-3 py-1 text-xs text-slate-600 hover:border-firm-500 hover:text-firm-700 dark:border-slate-500 dark:text-slate-300 dark:hover:text-firm-100"
          >
            + Add "{label}" rule
          </button>
        ))}
      </div>
    </div>
  );
}

function FieldDerivationRuleCard({
  rule,
  conditionFields,
  practiceGroups,
  areasOfLaw,
  storedFeeds,
  onPatch,
  onRemove,
}) {
  const inputClass = 'rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600';
  const targetField = fieldByPath(rule.targetPath, conditionFields);

  /**
   * The directories this rule can read, and what it can copy out of the one it has picked.
   *
   * The three built-in lists are curated allowlists — an admin should not have to know that a
   * practice group's leader is stored as `practiceGroupLeaderUid`. A feed's are the columns of
   * its own mapping, under the same names they were given over on the feeds page.
   */
  const lookupDirectories = [...LOOKUP_DIRECTORIES, ...feedLookupDirectories(storedFeeds)];
  const lookupFeed = lookupFeedId(rule.lookupIn);
  const lookupAttributes = lookupFeed
    ? feedLookupAttributes(storedFeeds, lookupFeed)
    : LOOKUP_ATTRIBUTES[rule.lookupIn] || [];

  /**
   * Choosing a picker as the source settles which directory the rule reads: the field itself
   * names the feed it picks from, and any other directory would find nothing. So it is set here
   * rather than left as a second question with exactly one right answer.
   */
  const chooseSource = (sourcePath) => {
    const field = fieldByPath(sourcePath, conditionFields);
    if (field?.type !== 'clientSearch') return onPatch({ sourcePath });
    const feedId = field.feedId || 'clients';
    const attributes = feedLookupAttributes(storedFeeds, feedId);
    return onPatch({
      sourcePath,
      lookupIn: feedLookupId(feedId),
      attribute: (attributes[0] && attributes[0][0]) || 'clientName',
      then: undefined,
    });
  };

  /**
   * "Don't allow changes" means two things at once, and both are worth stating: the field is
   * greyed out, AND the rule stops yielding to what the user did. Every kind has some form of
   * "back off once they have made it their own", and that is exactly what has to go — otherwise
   * the field would be read-only while the rule quietly declined to maintain it.
   */
  const lockNote =
    rule.kind === 'setDefault'
      ? 'While the condition matches, this field is greyed out and holds this value.'
      : rule.kind === 'mirror'
        ? 'This field is greyed out and always equals the source — it can no longer be edited away from it.'
        : 'This field is greyed out and always shows the looked-up value.';

  // Switching kind keeps id/enabled/targetPath and swaps in that kind's own fields —
  // the previous kind's fields are set to undefined (dropped on save, not written as
  // stale leftovers) rather than regenerating the rule (which would lose its id/target).
  const changeKind = (kind) => {
    const shared = { id: rule.id, kind, enabled: rule.enabled !== false, targetPath: rule.targetPath || '' };
    const cleared = { when: undefined, value: undefined, sourcePath: undefined, lookupIn: undefined, attribute: undefined, then: undefined };
    if (kind === 'setDefault') return onPatch({ ...cleared, ...shared, when: true, value: '' });
    if (kind === 'mirror' || kind === 'appendPeople') return onPatch({ ...cleared, ...shared, sourcePath: '' });
    return onPatch({ ...cleared, ...shared, sourcePath: '', lookupIn: 'roster', attribute: 'displayName' });
  };

  // What this rule does, in a sentence, above the controls that say the same thing in six
  // dropdowns. See lib/derivationSummary.js — it is pure and tested there, because a summary
  // that misdescribes a rule is worse than none: it is what someone reads before switching the
  // wrong one off.
  const summary = derivationRuleSummary(
    rule,
    (path) => fieldByPath(path, conditionFields),
    lookupAttributes
  );

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <p
        className={`text-sm font-medium ${
          rule.enabled === false
            ? 'text-slate-400 line-through dark:text-slate-500'
            : 'text-slate-800 dark:text-slate-100'
        }`}
      >
        {summary}
        {rule.lockTarget === true && (
          <span className="ml-2 rounded bg-firm-50 px-1.5 py-0.5 text-xs font-normal text-firm-700 dark:bg-firm-900 dark:text-firm-100">
            locked
          </span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <select className={inputClass} value={rule.kind} onChange={(e) => changeKind(e.target.value)}>
          {DERIVATION_RULE_KINDS.map(([kind, label]) => (
            <option key={kind} value={kind}>
              {label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={rule.enabled !== false}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
          />
          Enabled
        </label>
        {/*
          Offered only on the kinds where it means something. An appendPeople rule exists to ADD
          to whoever the user picked, so greying its target out would remove the only thing it does.
        */}
        {isLockableDerivationKind(rule.kind) && (
          <label
            className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300"
            title="The target field is greyed out on the form and this rule keeps its value"
          >
            <input
              type="checkbox"
              checked={rule.lockTarget === true}
              onChange={(e) => onPatch({ lockTarget: e.target.checked ? true : undefined })}
            />
            Don&apos;t allow changes
          </label>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto text-xs text-slate-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
        >
          Remove rule
        </button>
      </div>

      {rule.lockTarget === true && isLockableDerivationKind(rule.kind) && (
        <p className="rounded border-l-2 border-firm-500 bg-firm-50 px-2 py-1 text-xs text-firm-700 dark:bg-firm-900 dark:text-firm-100">
          {lockNote}
        </p>
      )}

      <label className="block text-xs text-slate-500 dark:text-slate-400">
        Field to fill in
        <select
          className={`${inputClass} mt-1 w-full`}
          value={rule.targetPath}
          onChange={(e) => onPatch({ targetPath: e.target.value })}
        >
          <option value="">Choose a field…</option>
          {conditionFields.map((f) => (
            <option key={f.path} value={f.path}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      {rule.kind === 'setDefault' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">When this becomes true:</p>
          <ConditionBuilder
            value={rule.when}
            onChange={(when) => onPatch({ when })}
            fields={conditionFields}
            practiceGroups={practiceGroups}
            areasOfLaw={areasOfLaw}
          />
          <DerivationValueInput
            field={targetField}
            value={rule.value}
            onChange={(value) => onPatch({ value })}
            practiceGroups={practiceGroups}
            areasOfLaw={areasOfLaw}
          />
        </div>
      )}

      {rule.kind === 'appendPeople' && (
        <label className="block text-xs text-slate-500 dark:text-slate-400">
          Add everyone picked in
          <select
            className={`${inputClass} mt-1 w-full`}
            value={rule.sourcePath || ''}
            onChange={(e) => onPatch({ sourcePath: e.target.value })}
          >
            <option value="">Choose a field…</option>
            {conditionFields
              .filter((f) => f.path !== rule.targetPath)
              .map((f) => (
                <option key={f.path} value={f.path}>
                  {f.label}
                </option>
              ))}
          </select>
          <span className="mt-0.5 block text-slate-400 dark:text-slate-500">
            People are only ever added, never removed — so the target keeps anyone the source
            later drops.
          </span>
        </label>
      )}
      {rule.kind === 'mirror' && (
        <label className="block text-xs text-slate-500 dark:text-slate-400">
          Copy the value from
          <select
            className={`${inputClass} mt-1 w-full`}
            value={rule.sourcePath || ''}
            onChange={(e) => onPatch({ sourcePath: e.target.value })}
          >
            <option value="">Choose a field…</option>
            {conditionFields
              .filter((f) => f.path !== rule.targetPath)
              .map((f) => (
                <option key={f.path} value={f.path}>
                  {f.label}
                </option>
              ))}
          </select>
        </label>
      )}

      {rule.kind === 'lookup' && (
        <div className="space-y-2">
          <label className="block text-xs text-slate-500 dark:text-slate-400">
            Based on the selection in
            <select
              className={`${inputClass} mt-1 w-full`}
              value={rule.sourcePath || ''}
              onChange={(e) => chooseSource(e.target.value)}
            >
              <option value="">Choose a field…</option>
              {conditionFields.map((f) => (
                <option key={f.path} value={f.path}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <select
              className={inputClass}
              value={rule.lookupIn || 'roster'}
              onChange={(e) => {
                const lookupIn = e.target.value;
                const attributes = lookupFeedId(lookupIn)
                  ? feedLookupAttributes(storedFeeds, lookupFeedId(lookupIn))
                  : LOOKUP_ATTRIBUTES[lookupIn] || [];
                onPatch({ lookupIn, attribute: (attributes[0] && attributes[0][0]) || '', then: undefined });
              }}
            >
              {lookupDirectories.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className={inputClass}
              value={rule.attribute || ''}
              onChange={(e) => onPatch({ attribute: e.target.value, then: undefined })}
            >
              {lookupAttributes.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {lookupFeed && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Read from the copy of the record stored when the client was chosen, so what a matter
              was filled in with is what it keeps — a later export changing this client&rsquo;s
              details will not rewrite a matter that is already filled in. What can be copied comes
              from this feed&rsquo;s own columns, in{' '}
              <strong>Admin → Directories → Feeds</strong>.
            </p>
          )}
          {rule.lookupIn === 'practiceGroups' && rule.attribute === 'practiceGroupLeaderUid' && (
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(rule.then)}
                onChange={(e) =>
                  onPatch({ then: e.target.checked ? { lookupIn: 'roster', attribute: 'displayName' } : undefined })
                }
              />
              Also look up that person's display name in Roster
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/** A single-value input for a setDefault rule's literal `value`, shaped by the target
 * field's `kind`/`options` — the same field-classification data ConditionBuilder's own
 * condition-value editor already uses, just rendered as one pick/type-in rather than a
 * multi-value match list, since this assigns a value instead of matching one. */
function DerivationValueInput({ field, value, onChange, practiceGroups, areasOfLaw }) {
  const inputClass = 'rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600';

  if (!field) {
    return <p className="text-xs text-slate-400 dark:text-slate-500">Choose a field above to set its value.</p>;
  }

  if (field.kind === 'boolean') {
    return (
      <label className="block text-xs text-slate-500 dark:text-slate-400">
        Set it to
        <select
          className={`${inputClass} mt-1 w-full`}
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => onChange(e.target.value === 'true')}
        >
          <option value="">Choose…</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
    );
  }

  const options = field.options || (field.optionsFrom ? (field.optionsFrom === 'practiceGroups' ? practiceGroups : areasOfLaw) || [] : []);
  if (options.length > 0) {
    return (
      <label className="block text-xs text-slate-500 dark:text-slate-400">
        Set it to
        <select className={`${inputClass} mt-1 w-full`} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name || o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="block text-xs text-slate-500 dark:text-slate-400">
      Set it to
      <input
        className={`${inputClass} mt-1 w-full`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Value to fill in"
      />
    </label>
  );
}

function SortableSection({
  section,
  onPatchSection,
  onSetSectionCondition,
  deleteWarningsFor,
  deleteDisabled,
  onRequestDeleteSection,
  onDeleteSection,
  pendingDelete,
  onCancelDelete,
  onPatch,
  onChangeType,
  onMove,
  onDelete,
  onMoveToSection,
  allSections,
  conditionFields,
  practiceGroups,
  areasOfLaw,
  roster,
  feeds,
  registerRef,
  isHighlighted,
}) {
  // The single, top-level SortableContext contains both section IDs and every movable
  // field ID. Collision filtering keeps the two interaction types isolated, while the
  // flat field membership lets a row cross page boundaries.
  const sortable = useSortable({
    id: sectionDragId(section.id),
    data: { type: 'section', sectionId: section.id },
  });
  const style = { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition };
  const setRefs = (node) => {
    sortable.setNodeRef(node);
    registerRef?.(node);
  };
  return (
    <section
      ref={setRefs}
      style={style}
      className={`rounded-lg bg-white p-4 shadow transition-colors dark:bg-slate-900 ${section.isActive ? '' : 'opacity-60'} ${isHighlighted ? 'ring-2 ring-firm-500 bg-firm-50 dark:bg-firm-950' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          {...sortable.attributes}
          {...sortable.listeners}
          className="cursor-grab rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing dark:hover:bg-slate-800 dark:hover:text-slate-200"
          title="Drag to reorder page"
          aria-label={`Drag to reorder ${section.title}`}
        >
          ⠿
        </button>
        <input
          className="min-w-48 flex-1 rounded border border-transparent bg-transparent px-2 py-1 text-sm font-semibold text-slate-800 hover:border-slate-300 focus:border-firm-500 focus:outline-none dark:text-slate-100 dark:hover:border-slate-600"
          value={section.title}
          onChange={(event) => onPatchSection(section.id, 'title', event.target.value)}
          aria-label={`Page title for ${section.id}`}
        />
        <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={section.isActive}
            onChange={(event) => onPatchSection(section.id, 'isActive', event.target.checked)}
          />
          Show page
        </label>
        {section.isCustom ? (
          pendingDelete ? (
            <>
              <button
                type="button"
                onClick={onDeleteSection}
                className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950"
              >
                Delete empty page
              </button>
              <button
                type="button"
                onClick={onCancelDelete}
                className="px-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onRequestDeleteSection}
              className="px-1 text-xs text-red-600 hover:underline dark:text-red-400"
            >
              Delete page
            </button>
          )
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500" title="Code-defined pages can be retired, not deleted.">
            Code page — use Show page to retire
          </span>
        )}
      </div>
      <input
        className="mt-1 w-full rounded border border-transparent bg-transparent px-2 py-1 text-xs text-slate-600 hover:border-slate-300 focus:border-firm-500 focus:outline-none dark:text-slate-300 dark:hover:border-slate-600"
        value={section.description || ''}
        placeholder="Page description shown under the title (optional)"
        onChange={(event) => onPatchSection(section.id, 'description', event.target.value)}
        aria-label={`Page description for ${section.id}`}
      />
      <details className="mt-2 rounded border border-slate-200 p-2 text-xs dark:border-slate-700">
        <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
          Page visibility
          {section.visibleIf !== undefined && section.visibleIf !== true && (
            <span className="ml-1 rounded bg-firm-50 px-1 text-firm-800 dark:bg-firm-900 dark:text-firm-100">
              conditional
            </span>
          )}
        </summary>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Show this page only when the condition is met. Leave it empty to always show the
          page. A condition set here replaces any rule the page shipped with.
        </p>
        <div className="mt-2">
          <ConditionBuilder
            value={section.visibleIf}
            onChange={(visibleIf) => onSetSectionCondition(section.id, visibleIf)}
            fields={conditionFields}
            practiceGroups={practiceGroups}
            areasOfLaw={areasOfLaw}
          />
        </div>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => onSetSectionCondition(section.id, true)}
            className="text-firm-700 hover:underline dark:text-firm-100"
          >
            Always show this page
          </button>
          <button
            type="button"
            onClick={() => onSetSectionCondition(section.id, undefined)}
            className="text-slate-500 hover:underline dark:text-slate-400"
          >
            Reset to default
          </button>
        </div>
      </details>
      {/*
        Explicit widths on every column except Field: the Field cell holds a whole stack of
        controls, so left to itself it wins the auto-layout fight and squeezes Help text
        down to a few unusable pixels. The two flag columns are sized to their checkbox
        (their headers wrap instead of forcing ~80px each) rather than to their labels.
      */}
      <table className="mt-2 w-auto table-fixed text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
            <th className="w-7 py-1" aria-label="Move" />
            <th className="w-8 py-1">On</th>
            {/*
              Field is a FIXED width now — the width of a field label — rather than the column that
              soaked up every spare pixel in the table. Help text is the one left flexible, so the
              slack lands somewhere useful instead of as dead space beside every label.

              Requirement and Move to page are wide enough to READ the selected option: at w-28
              they rendered "Use field rul…" and "Client & Mat…". The two flag columns get their
              own width rather than sitting shoulder to shoulder.
            */}
            <th className="w-[460px] py-1">Field</th>
            <th className="w-40 py-1">Requirement</th>
            <th className="w-56 py-1">Move to page</th>
            <th className="w-16 py-1" />
          </tr>
        </thead>
        <DroppableFieldBody sectionId={section.id}>
          {section.fields.map((field, index) => (
            <SortableFieldRow
              key={field.isCustom ? field.path : `${field.path || 'notice'}-${index}`}
              field={field}
              sectionId={section.id}
              onPatch={(key, value) => onPatch(field, key, value)}
              onChangeType={(nextType) => onChangeType(field, nextType)}
              onMove={(delta) => onMove(field, delta)}
              onDelete={() => onDelete(field)}
              deleteWarnings={deleteWarningsFor ? deleteWarningsFor(field) : []}
              deleteDisabled={deleteDisabled}
              onMoveToSection={(destinationId) => onMoveToSection(field, destinationId)}
              allSections={allSections}
              conditionFields={conditionFields}
              practiceGroups={practiceGroups}
              areasOfLaw={areasOfLaw}
              roster={roster}
              feeds={feeds}
            />
          ))}
        </DroppableFieldBody>
      </table>
    </section>
  );
}

function DroppableFieldBody({ sectionId, children }) {
  const { setNodeRef } = useDroppable({
    id: `section-drop:${sectionId}`,
    data: { type: 'field-container', sectionId },
  });
  return <tbody ref={setNodeRef} className="divide-y divide-slate-100 dark:divide-slate-800">{children}</tbody>;
}

/**
 * What is configured on a field, for a row nobody is editing. Only what is actually SET appears —
 * a row with nothing configured shows nothing, which is what makes a page of plain fields quiet.
 */
function FieldConfigChips({ field }) {
  const chips = [];
  if (field.visibleIf !== undefined) chips.push(['Conditional', 'Only shown when a condition matches']);
  if (field.disabledOverride === true) {
    chips.push(['Greyed out', 'Always read-only']);
  } else if (field.disabledIf !== undefined) {
    chips.push(['Greyed when…', 'Read-only when a condition matches']);
  }
  if (isUsableDefault(field.defaultValue)) {
    chips.push([`Starts as ${defaultValueText(field.defaultValue)}`, 'Starting value']);
  }
  if (field.format) chips.push(['Formatted', 'Custom text formatting']);
  if (chips.length === 0) return null;
  return (
    <>
      {chips.map(([text, title]) => (
        <span
          key={text}
          title={title}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {text}
        </span>
      ))}
    </>
  );
}
function SortableFieldRow({
  field,
  sectionId,
  roster,
  feeds,
  onPatch,
  onChangeType,
  onMove,
  onDelete,
  deleteWarnings = [],
  deleteDisabled = false,
  onMoveToSection,
  allSections,
  conditionFields,
  practiceGroups,
  areasOfLaw,
}) {
  const movable = canMove(field);
  // Two-step delete for ANY field; the confirmation names affected approval steps /
  // reveal rules so removing a governance-referenced field is deliberate, never silent.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /**
   * The four expandable editors (visibility, grey-out, starting value, formatting) sit behind one
   * toggle rather than all being on screen at once. Four closed boxes per row read as four things
   * needing attention, and a page of fields became a wall of them — while what a reader actually
   * wants from a row they are not editing is "what is configured here", which the chips answer in
   * a fraction of the space.
   */
  const [editing, setEditing] = useState(false);
  const sortable = useSortable({
    id: fieldDragId(field.path || `${sectionId}:${field.type}`),
    disabled: !movable,
    data: { type: 'field', path: field.path, sectionId },
  });
  const style = movable ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition } : undefined;

  if (!movable) {
    return (
      <tr className="text-slate-400 dark:text-slate-500">
        <td className="py-1" />
        <td className="py-1" />
        <td className="py-1" colSpan={7}>
          <em>{field.type === 'notice' ? field.text : field.label}</em>
          <span className="ml-2 text-xs">— pinned {field.type} block</span>
        </td>
      </tr>
    );
  }

  const isNotice = field.type === 'notice';
  // Every movable field — hardcoded/base or custom — can carry a visibleIf override.
  // fieldLayout.js's mergeBaseField (and effectiveLayout.js's server-side mirror) already
  // apply overrides[path].visibleIf to base fields exactly like custom ones; this used to
  // be gated to isCustom/notice fields only, which was just an incomplete rollout of the
  // editor, not a real limit in the underlying data model or evaluator.
  const supportsCondition = true;
  /**
   * The editors live in their OWN full-width row beneath the field, not inside the field cell.
   * The table is table-fixed, so anything in that cell is capped at the column width — and the
   * Field column was 735px wide on every row precisely because it had to hold these. Moving them
   * out is what lets that column be the width of a field label instead of the width of an editor.
   */
  const fieldEditors = (
    <div className="flex flex-wrap items-start gap-2">
      <div className="w-full rounded border border-slate-200 p-1.5 text-xs dark:border-slate-700">
        <p className="mb-1 font-medium text-slate-500 dark:text-slate-400">Placement</p>
        <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
              {field.isCustom && (
                <label className="flex items-center gap-1">
                  Type
                  <select
                    className="rounded border border-slate-300 px-1 py-0.5 text-xs dark:border-slate-600"
                    value={field.type}
                    onChange={(event) => onChangeType(event.target.value)}
                    aria-label={`Field type for ${field.label || field.path}`}
                    title="Change this field's type without deleting and recreating it"
                  >
                    {CUSTOM_FIELD_TYPES.map(([value, text]) => (
                      <option key={value} value={value}>{text}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-1">
                Width
                <select
                  className="rounded border border-slate-300 px-1 py-0.5 text-xs dark:border-slate-600"
                  value={fieldWidth(field)}
                  onChange={(event) => onPatch('width', event.target.value)}
                  aria-label={`Width for ${field.label || field.text || field.path}`}
                >
                  {FIELD_WIDTHS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1" title="Start this field on a new line">
                <input
                  type="checkbox"
                  checked={field.newRow === true}
                  onChange={(event) => onPatch('newRow', event.target.checked)}
                  aria-label={`Start ${field.label || field.text || field.path} on a new line`}
                />
                New line
              </label>
        </div>
      </div>
      <div className="w-full rounded border border-slate-200 p-1.5 text-xs dark:border-slate-700">
        <p className="mb-1 font-medium text-slate-500 dark:text-slate-400">Help text and prompts</p>
        <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
          <div className="w-full max-w-xl">
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                  placeholder="Help text shown under the field"
                  value={field.helpOverride || ''}
                  onChange={(event) => onPatch('helpText', event.target.value)}
                />
                {field.type === 'attachments' && (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Category: <code>{field.category}</code> (set when this upload block was created)
                  </p>
                )}
                {SUPPORTS_PLACEHOLDER.includes(field.type) && (
                  <input
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                    placeholder="Unselected prompt (e.g. Select attorney…)"
                    value={field.placeholder || ''}
                    onChange={(event) => onPatch('placeholder', event.target.value)}
                    aria-label={`Unselected prompt for ${field.label || field.path}`}
                  />
                )}
                {/* Controls that grow rows carry an Add button whose label is now relabelable. */}
                {(field.type === 'repeatable' || field.type === 'originationSplit') && (
                  <input
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                    placeholder={`Add-button label (e.g. Add ${(field.label || 'entry').toLowerCase()})`}
                    value={field.addLabel || ''}
                    onChange={(event) => onPatch('addLabel', event.target.value || undefined)}
                    aria-label={`Add-button label for ${field.label || field.path}`}
                  />
                )}
                {field.type === 'clientSearch' && (
                  <label className="mt-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    Directory
                    <select
                      className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs"
                      value={field.feedId || 'clients'}
                      onChange={(event) =>
                        onPatch('feedId', event.target.value === 'clients' ? undefined : event.target.value)
                      }
                      aria-label={`Directory searched by ${field.label || field.path}`}
                    >
                      {allFeeds(feeds?.feeds)
                        .filter((entry) => entry.columnMapped)
                        .map((entry) => (
                          <option key={entry.id} value={entry.id}>
                            {entry.label}
                          </option>
                        ))}
                    </select>
                    <span className="text-slate-400 dark:text-slate-500">
                      which imported directory this field searches
                    </span>
                  </label>
                )}
                {PEOPLE_CHOOSER_TYPES.includes(field.type) && (
                  <TitleFilterEditor
                    field={field}
                    roster={roster}
                    feeds={feeds}
                    onChange={(next) => onPatch('titleFilter', next.length ? next : undefined)}
                  />
                )}
                {customFieldTypeNeedsRange(field.type) && (
                  <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                    <span>Range</span>
                    {[
                      ['min', 'Min', PERCENT_DEFAULTS.min],
                      ['max', 'Max', PERCENT_DEFAULTS.max],
                      ['decimals', 'Decimals', PERCENT_DEFAULTS.decimals],
                    ].map(([key, label, fallback]) => (
                      <input
                        key={key}
                        type="number"
                        className="w-16 rounded border border-slate-300 px-1 py-0.5 text-xs dark:border-slate-600"
                        placeholder={String(fallback)}
                        value={typeof field[key] === 'number' ? field[key] : ''}
                        onChange={(event) =>
                          onPatch(key, event.target.value === '' ? undefined : Number(event.target.value))
                        }
                        aria-label={`${label} for ${field.label || field.path}`}
                        title={label}
                      />
                    ))}
                  </div>
                )}
          </div>
        </div>
      </div>
      <div className="w-full rounded border border-slate-200 p-1.5 text-xs dark:border-slate-700">
        <p className="mb-1 font-medium text-slate-500 dark:text-slate-400">Flags</p>
        <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
          <label className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={field.preventClone}
              onChange={(event) => onPatch('preventClone', event.target.checked)}
            />
            Never copy (excluded from Clone this matter and templates)
          </label>
          <label className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={field.verificationRequired}
              onChange={(event) => onPatch('verificationRequired', event.target.checked)}
            />
            Verification required (adds it to the pre-submit checklist)
          </label>
        </div>
      </div>
        {supportsCondition && (
          <details className="grow rounded border border-slate-200 p-1.5 text-xs open:w-full dark:border-slate-700">
            <summary className="cursor-pointer font-medium text-firm-700 dark:text-firm-100">
              {field.visibleIf === undefined ? 'Add conditional visibility' : 'Edit conditional visibility'}
            </summary>
            <div className="mt-1">
              <p className="mb-1 text-slate-500 dark:text-slate-400">
                Show this field only when the selected rules match. Fields from every page are available.
              </p>
              <ConditionBuilder
                value={field.visibleIf}
                onChange={(visibleIf) => onPatch('visibleIf', visibleIf === true ? undefined : visibleIf)}
                fields={conditionFields.filter((candidate) => candidate.path !== field.path)}
                practiceGroups={practiceGroups}
                areasOfLaw={areasOfLaw}
              />
              {field.visibleIf !== undefined && (
                <button
                  type="button"
                  onClick={() => onPatch('visibleIf', undefined)}
                  className="mt-1 text-xs text-red-600 hover:underline dark:text-red-400"
                >
                  Remove visibility condition
                </button>
              )}
            </div>
          </details>
        )}
        {/*
          Greying out, and the logic behind it. Deliberately next to conditional visibility
          rather than on the Field Rules page: "when is this hidden" and "when is this read-only"
          are the same question asked twice, and answering them in two different screens is how
          they end up contradicting each other.
        */}
        {supportsCondition && (
          <details className="grow rounded border border-slate-200 p-1.5 text-xs open:w-full dark:border-slate-700">
            <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
              {field.disabledOverride === true
                ? 'Always greyed out'
                : field.disabledIf !== undefined
                  ? 'Edit when it is greyed out'
                  : 'Grey out (read-only)'}
            </summary>
            <div className="mt-1">
              <p className="mb-1 text-slate-500 dark:text-slate-400">
                A greyed-out field still shows on the form and still prints — it just cannot be
                edited. Use it for anything a feed, an auto-fill rule or firm policy fills in.
              </p>
              <label className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={field.disabledOverride === true}
                  onChange={(event) => {
                    onPatch('disabled', event.target.checked ? true : undefined);
                    // Always-on and a condition are two answers to one question. Keeping both
                    // would leave a condition that silently does nothing.
                    if (event.target.checked) onPatch('disabledIf', undefined);
                  }}
                />
                Always greyed out
              </label>
              {field.disabledOverride !== true && (
                <div className="mt-1.5">
                  <p className="mb-1 text-slate-500 dark:text-slate-400">
                    Or grey it out only when these rules match:
                  </p>
                  <ConditionBuilder
                    value={field.disabledIf}
                    onChange={(disabledIf) =>
                      onPatch('disabledIf', disabledIf === true ? undefined : disabledIf)
                    }
                    fields={conditionFields.filter((candidate) => candidate.path !== field.path)}
                    practiceGroups={practiceGroups}
                    areasOfLaw={areasOfLaw}
                  />
                  {field.disabledIf !== undefined && (
                    <button
                      type="button"
                      onClick={() => onPatch('disabledIf', undefined)}
                      className="mt-1 text-xs text-red-600 hover:underline dark:text-red-400"
                    >
                      Remove greyed-out condition
                    </button>
                  )}
                </div>
              )}
              {/*
                A field nobody can fill, that nothing fills for them, and that blocks submission,
                is a form nobody can submit. Said at the moment the combination is chosen.
              */}
              {field.disabledOverride === true &&
                field.requirementOverride === 'required' &&
                !isUsableDefault(field.defaultValue) && (
                  <p className="mt-1.5 text-red-600 dark:text-red-400">
                    Required, always greyed out, and no starting value — nobody can submit unless
                    an auto-fill rule sets this.
                  </p>
                )}
            </div>
          </details>
        )}

        {/*
          The starting value, and the logic behind it. Filled in when a matter is created, or the
          moment the field (and its condition) starts applying — never afterwards, so clearing
          the box leaves it cleared.
        */}
        {SUPPORTS_DEFAULT.includes(field.type) && (
          <details className="grow rounded border border-slate-200 p-1.5 text-xs open:w-full dark:border-slate-700">
            <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
              {isUsableDefault(field.defaultValue)
                ? `Starts as: ${defaultValueText(field.defaultValue)}`
                : 'Starting value'}
            </summary>
            <div className="mt-1">
              <p className="mb-1 text-slate-500 dark:text-slate-400">
                Filled in when a matter is created, or when this field first appears. It is a
                starting value, not a fixed one — clearing it on the form leaves it cleared, and
                it never overwrites an answer that is already there.
              </p>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                placeholder={field.type === 'yesno' ? 'Yes / No' : 'Starting value'}
                value={defaultValueText(field.defaultValue)}
                onChange={(event) =>
                  onPatch('defaultValue', parseDefaultValue(field, event.target.value))
                }
                aria-label={`Starting value for ${field.label || field.path}`}
              />
              {isUsableDefault(field.defaultValue) && (
                <div className="mt-1.5">
                  <p className="mb-1 text-slate-500 dark:text-slate-400">
                    Only apply it when these rules match (leave empty to always apply it):
                  </p>
                  <ConditionBuilder
                    value={field.defaultIf}
                    onChange={(defaultIf) =>
                      onPatch('defaultIf', defaultIf === true ? undefined : defaultIf)
                    }
                    fields={conditionFields.filter((candidate) => candidate.path !== field.path)}
                    practiceGroups={practiceGroups}
                    areasOfLaw={areasOfLaw}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      onPatch('defaultValue', undefined);
                      onPatch('defaultIf', undefined);
                    }}
                    className="mt-1 text-xs text-red-600 hover:underline dark:text-red-400"
                  >
                    Remove starting value
                  </button>
                </div>
              )}
            </div>
          </details>
        )}
        {(hasEditableOptions(field) || field.type !== 'attachments') && (
          <details className="grow rounded border border-slate-200 p-1.5 text-xs open:w-full dark:border-slate-700">
            <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
              {hasEditableOptions(field) ? 'Choices & formatting' : field.type === 'repeatable' ? 'Columns & formatting' : isNotice ? 'Text formatting' : 'Formatting'}
            </summary>
            <div className="mt-1 flex flex-wrap items-start gap-4">
              {hasEditableOptions(field) && (
                <OptionRowsEditor
                  options={field.options}
                  minimumOptions={field.type === 'radio' ? 2 : 1}
                  autoSlug={false}
                  onChange={(next) => onPatch('options', next)}
                />
              )}
              {/* Columns are editable on BUILT-IN repeating groups too, stored as an
                  override so the code-shipped columns are always restorable. */}
              {field.type === 'repeatable' && (
                <div>
                  <SubFieldRowsEditor
                    fields={field.fields}
                    onChange={(next) => onPatch('fields', next)}
                  />
                  {!field.isCustom && field.fieldsOverridden && (
                    <button
                      type="button"
                      onClick={() => onPatch('fields', undefined)}
                      className="mt-1 text-xs text-slate-500 hover:underline dark:text-slate-400"
                    >
                      Restore built-in columns
                    </button>
                  )}
                </div>
              )}
              {field.type !== 'attachments' && (
                <FormatControls
                  field={field}
                  textOnly={isNotice}
                  onPatch={(patch) => Object.entries(patch).forEach(([key, value]) => onPatch(key, value))}
                />
              )}
            </div>
          </details>
        )}
    </div>
  );

  return (
    <>
    <tr ref={sortable.setNodeRef} style={style} className={field.isActive ? '' : 'opacity-50'}>
      <td className="py-1.5 align-top">
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          {...sortable.attributes}
          {...sortable.listeners}
          className="cursor-grab rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing dark:hover:bg-slate-800 dark:hover:text-slate-200"
          title="Drag to reorder or move to another page"
          aria-label={`Drag ${field.label || field.text || field.path}`}
        >
          ⠿
        </button>
      </td>
      <td className="py-1.5 align-top">
        <input type="checkbox" checked={field.isActive} onChange={(event) => onPatch('isActive', event.target.checked)} />
      </td>
      <td className="py-1.5 pr-3 align-top">
        {isNotice ? (
          <>
            <label className="block text-xs text-slate-600 dark:text-slate-300">
              Notice message
              <textarea
                rows={4}
                className="mt-0.5 w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                value={field.text || ''}
                onChange={(event) => onPatch('text', event.target.value)}
                aria-label={`Notice message for ${field.label}`}
              />
            </label>
            <label className="mt-0.5 block text-xs text-slate-600 dark:text-slate-300">
              Builder label
              <input
                className="mt-0.5 w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                value={field.label || ''}
                onChange={(event) => onPatch('label', event.target.value)}
                aria-label={`Builder label for ${field.path}`}
              />
            </label>
          </>
        ) : (
          <input
            className="w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
            value={field.label}
            onChange={(event) => onPatch('label', event.target.value)}
          />
        )}
        {/*
          One wrapping meta row instead of four stacked ones (path, type, new-line, width).
          Each of those was a full-height line of its own for a single small control, which
          is most of why a page of fields scrolled as far as it did.
        */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600 dark:text-slate-300">
          <code className="text-slate-400 dark:text-slate-500">
            {field.path}
            {!field.isCustom && <> · {field.type}</>}
            {field.isCustom && <span className="ml-1 text-firm-700 dark:text-firm-100">custom</span>}
          </code>
        </div>
        {/*
          The two expandable editors sit on ONE line while closed (each shrinks to its
          summary text) and go full width once opened, so a closed row costs one line
          instead of two.
        */}
        {/*
          Collapsed: say what is configured and offer one way in. Expanded: the editors, which are
          unchanged — this is a disclosure change, not a rewrite of what they do.
        */}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setEditing((was) => !was)}
            className="rounded border border-slate-300 px-2 py-0.5 font-medium text-firm-700 hover:border-firm-500 dark:border-slate-600 dark:text-firm-100"
            aria-expanded={editing}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
          {!editing && <FieldConfigChips field={field} />}
        </div>
      </td>
      {isNotice ? (
        <>
          <td className="w-28 py-1.5 pr-3 align-top">
            <label className="text-xs text-slate-600 dark:text-slate-300">
              Tone
              <select
                className="mt-0.5 w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                value={field.variant || 'neutral'}
                onChange={(event) => onPatch('variant', event.target.value)}
                aria-label={`Notice tone for ${field.label}`}
              >
                {NOTICE_VARIANTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </td>
        </>
      ) : (
        <>
          <td className="w-28 py-1.5 pr-3 align-top">
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={field.requirementOverride || ''}
              onChange={(event) => onPatch('requirement', event.target.value)}
            >
              {LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </td>
        </>
      )}
      <td className="py-1.5 pr-3 align-top">
        <select
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
          value={sectionId}
          onChange={(event) => onMoveToSection(event.target.value)}
          title="Move this field to another page"
          aria-label={`Move ${field.label} to another page`}
        >
          {allSections.map((section) => <option key={section.id} value={section.id}>{section.title}</option>)}
        </select>
      </td>
      <td className="py-1.5 text-right align-top">
        <button type="button" onClick={() => onMove(-1)} className="px-1 text-slate-500 hover:text-firm-700 dark:text-slate-400 dark:hover:text-firm-100" title="Move up">↑</button>
        <button type="button" onClick={() => onMove(1)} className="px-1 text-slate-500 hover:text-firm-700 dark:text-slate-400 dark:hover:text-firm-100" title="Move down">↓</button>
        {confirmingDelete ? (
          <span className="ml-1 inline-flex flex-col items-end gap-1 text-left text-xs">
            <span className="max-w-64 text-red-700 dark:text-red-300">
              Delete “{field.label || field.text || field.path}”?
              {deleteWarnings.map((warning) => (
                <span key={warning} className="block">⚠ {warning}</span>
              ))}
              {!field.isCustom && (
                <span className="block text-slate-500 dark:text-slate-400">
                  Restorable from “Deleted fields” below.
                </span>
              )}
            </span>
            <span className="inline-flex gap-1">
              <button
                type="button"
                onClick={() => { setConfirmingDelete(false); onDelete(); }}
                disabled={deleteDisabled}
                title={deleteDisabled ? 'Checking approval-rule references…' : undefined}
                className="rounded bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="rounded border border-slate-300 px-2 py-0.5 text-xs dark:border-slate-600"
              >
                Cancel
              </button>
            </span>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="ml-1 text-xs text-red-600 hover:underline dark:text-red-400"
          >
            Delete
          </button>
        )}
      </td>
    </tr>
    {editing && (
      <tr className={field.isActive ? '' : 'opacity-50'}>
        <td />
        <td />
        <td colSpan={4} className="pb-3 pr-3">
          {fieldEditors}
        </td>
      </tr>
    )}
    </>
  );
}

/**
 * "Limit to titles" for a people chooser: chips of every distinct roster title token
 * (lib/peopleTitles.js), toggled into field.titleFilter. Nothing selected = everyone —
 * the historical unfiltered chooser. Tokens already saved on the field but no longer
 * present on any roster row still render (distinctTitleTokens folds them in), so a stale
 * filter stays visible and removable instead of becoming invisible configuration.
 */
function TitleFilterEditor({ field, roster, onChange }) {
  const selected = Array.isArray(field.titleFilter) ? field.titleFilter : [];
  const options = distinctTitleTokens(roster || [], selected);
  const selectedLower = new Set(selected.map((t) => t.trim().toLowerCase()));
  const toggle = (title) => {
    const lower = title.toLowerCase();
    onChange(
      selectedLower.has(lower)
        ? selected.filter((t) => t.trim().toLowerCase() !== lower)
        : [...selected, title]
    );
  };
  if (options.length === 0) return null;
  return (
    <details className="mt-1 rounded border border-slate-200 p-1.5 text-xs dark:border-slate-700">
      <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
        Limit to titles{selected.length > 0 && (
          <span className="ml-1 rounded bg-firm-50 px-1 text-firm-800 dark:bg-firm-900 dark:text-firm-100">
            {selected.length}
          </span>
        )}
      </summary>
      <p className="mt-1 text-slate-500 dark:text-slate-400">
        Only people whose roster title matches a selected title appear in this chooser.
        Combined titles count for each part ("Legal Secretary/Paralegal" matches both).
        Nothing selected = everyone.
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        {options.map((title) => (
          <label
            key={title}
            className={`flex cursor-pointer items-center gap-1 rounded border px-2 py-0.5 ${
              selectedLower.has(title.toLowerCase())
                ? 'border-firm-700 bg-firm-50 text-firm-800 dark:border-firm-500 dark:bg-firm-900 dark:text-firm-100'
                : 'border-slate-300 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300'
            }`}
          >
            <input
              type="checkbox"
              className="hidden"
              checked={selectedLower.has(title.toLowerCase())}
              onChange={() => toggle(title)}
            />
            {title}
          </label>
        ))}
      </div>
    </details>
  );
}

function AddFieldForm({
  sections,
  onAdd,
  conditionFields,
  practiceGroups,
  areasOfLaw,
  roster,
  existingAttachmentCategories,
}) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [sectionId, setSectionId] = useState(sections[0].id);
  const [required, setRequired] = useState(false);
  const [noticeText, setNoticeText] = useState('');
  const [noticeVariant, setNoticeVariant] = useState('warning');
  const [visibleIf, setVisibleIf] = useState(undefined);
  const [newRow, setNewRow] = useState(false);
  const [fullWidth, setFullWidth] = useState(false);
  const [helpText, setHelpText] = useState('');
  const [category, setCategory] = useState('');
  const [categoryEdited, setCategoryEdited] = useState(false);
  const [optionRows, setOptionRows] = useState([
    { label: '', value: '' },
    { label: '', value: '' },
  ]);
  const [subFieldRows, setSubFieldRows] = useState([]);
  const [addLabel, setAddLabel] = useState('');
  const [titleFilter, setTitleFilter] = useState([]);
  const needsOptions = customFieldTypeNeedsOptions(type);
  const needsSubFields = customFieldTypeNeedsSubFields(type);
  // Only complete columns count; a group must ship at least one usable column.
  const subFields = sanitizeSubFieldRows(subFieldRows);
  const hasValidSubFields = subFields.length >= 1;
  const options = optionRows
    .map((option) => ({ label: option.label.trim(), value: option.value.trim() }))
    .filter((option) => option.label && option.value);
  const minimumOptions = type === 'radio' ? 2 : 1;
  const hasValidOptions = options.length >= minimumOptions && options.length === optionRows.length;
  const isNotice = type === 'notice';
  const isAttachments = type === 'attachments';
  const normalizedCategory = attachmentCategorySlug(category);
  const categoryConflict = isAttachments && normalizedCategory && !isUniqueAttachmentCategory(
    normalizedCategory,
    existingAttachmentCategories
  );
  const canSubmit =
    label.trim() &&
    (!needsOptions || hasValidOptions) &&
    (!needsSubFields || hasValidSubFields) &&
    (!isNotice || noticeText.trim()) &&
    (!isAttachments || (normalizedCategory && !categoryConflict));

  const submit = () => {
    if (!canSubmit) return;
    onAdd({
      label,
      type,
      sectionId,
      required: !isNotice && !isAttachments && required,
      options,
      text: noticeText,
      variant: noticeVariant,
      visibleIf,
      newRow,
      fullWidth,
      helpText,
      category: normalizedCategory,
      fields: subFields,
      addLabel: addLabel.trim(),
      titleFilter,
    });
    setLabel('');
    setNoticeText('');
    setNoticeVariant('warning');
    setVisibleIf(undefined);
    setNewRow(false);
    setFullWidth(false);
    setHelpText('');
    setCategory('');
    setCategoryEdited(false);
    setOptionRows([{ label: '', value: '' }, { label: '', value: '' }]);
    setSubFieldRows([]);
    setAddLabel('');
    setTitleFilter([]);
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">+ Add field</h2>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600 dark:text-slate-300">Label
          <input
            className="mt-0.5 block w-64 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
            value={label}
            onChange={(event) => {
              const next = event.target.value;
              setLabel(next);
              if (isAttachments && !categoryEdited) setCategory(attachmentCategorySlug(next));
            }}
            placeholder={isNotice ? 'Internal builder label' : isAttachments ? 'Supporting documents' : 'Referral source detail'}
            aria-label="New field label"
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">Type
          <select
            className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
            value={type}
            onChange={(event) => {
              const next = event.target.value;
              setType(next);
              if (next !== 'attachments') setCategoryEdited(false);
              if (next === 'attachments' && !categoryEdited) setCategory(attachmentCategorySlug(label));
            }}
            aria-label="New field type"
          >
            {CUSTOM_FIELD_TYPES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">Page
          <select className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600" value={sectionId} onChange={(event) => setSectionId(event.target.value)}>
            {sections.map((section) => <option key={section.id} value={section.id}>{section.title}</option>)}
          </select>
        </label>
        {needsOptions && (
          <OptionRowsEditor
            options={optionRows}
            onChange={setOptionRows}
            minimumOptions={minimumOptions}
          />
        )}
        {needsSubFields && (
          <>
            <SubFieldRowsEditor fields={subFieldRows} onChange={setSubFieldRows} autoSlug />
            <label className="text-xs text-slate-600 dark:text-slate-300">Add button label
              <input
                className="mt-0.5 block w-48 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                value={addLabel}
                onChange={(event) => setAddLabel(event.target.value)}
                placeholder={label ? `Add another ${label.toLowerCase()}` : 'Add another entry'}
              />
            </label>
          </>
        )}
        {/* Choose WHO a people picker offers (by roster title: attorney vs secretary vs
            paralegal, …) right at insert time, instead of adding the field first and
            hunting down its row to filter it afterwards. */}
        {PEOPLE_CHOOSER_TYPES.includes(type) && (
          <div className="w-full">
            <TitleFilterEditor field={{ titleFilter }} roster={roster} onChange={setTitleFilter} />
          </div>
        )}
        {!isNotice && !isAttachments && <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300"><input type="checkbox" checked={required} onChange={(event) => setRequired(event.target.checked)} />Required</label>}
        <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={newRow}
            onChange={(event) => setNewRow(event.target.checked)}
            aria-label="Start new field on a new line"
          />
          Start this field on a new line
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={fullWidth}
            onChange={(event) => setFullWidth(event.target.checked)}
            aria-label="Span new field entire line"
          />
          Span entire line
        </label>
        <button type="button" onClick={submit} disabled={!canSubmit} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-40 dark:border-slate-600 dark:hover:bg-slate-800">Add</button>
      </div>
      {isNotice && (
        <div className="mt-3 space-y-2">
          <label className="block max-w-2xl text-xs text-slate-600 dark:text-slate-300">
            Notice message
            <textarea
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={noticeText}
              onChange={(event) => setNoticeText(event.target.value)}
              aria-label="New notice message"
            />
          </label>
          <label className="block w-52 text-xs text-slate-600 dark:text-slate-300">
            Tone
            <select className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600" value={noticeVariant} onChange={(event) => setNoticeVariant(event.target.value)} aria-label="New notice tone">
              {NOTICE_VARIANTS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
          </label>
          <details className="max-w-2xl rounded border border-slate-200 p-2 text-xs dark:border-slate-700">
            <summary className="cursor-pointer font-medium text-firm-700 dark:text-firm-100">Optional conditional visibility</summary>
            <div className="mt-2">
              <ConditionBuilder
                value={visibleIf}
                onChange={(next) => setVisibleIf(next === true ? undefined : next)}
                fields={conditionFields}
                practiceGroups={practiceGroups}
                areasOfLaw={areasOfLaw}
              />
            </div>
          </details>
        </div>
      )}
      {isAttachments && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-600 dark:text-slate-300">Category slug
            <input
              className="mt-0.5 block w-64 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setCategoryEdited(true);
              }}
              placeholder="supporting_documents"
              aria-label="New attachment category slug"
            />
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300">Help text
            <input
              className="mt-0.5 block w-80 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
              value={helpText}
              onChange={(event) => setHelpText(event.target.value)}
              placeholder="What should be uploaded here?"
              aria-label="New attachment help text"
            />
          </label>
          {categoryConflict && <p className="text-xs text-red-700 dark:text-red-300" role="alert">This category is already in use. Choose a unique upload category.</p>}
        </div>
      )}
      {needsSubFields && !hasValidSubFields && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          Add at least one column — each needs a label. Columns are the inputs repeated on every entry.
        </p>
      )}
      {needsOptions && !hasValidOptions && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          Add {minimumOptions === 2 ? 'at least two choices' : 'a choice'}, each with a label and stored value.
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">New fields are stored at <code>custom.&lt;slug&gt;</code> so they cannot collide with a real matter field. New attachment categories must also be unique. Remember to save the layout.</p>
    </div>
  );
}

